import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  adminNotificationService,
  FormattedAdminNotification,
  AdminNotificationCounts,
  AdminNotificationCategory,
  ADMIN_NOTIFICATION_TYPES,
  AdminNotificationType,
} from "@/lib/services/admin-notification.service";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * useAdminNotifications - Hook for admin notifications with real-time updates
 * KISS: Simple hook that manages admin notifications state
 * SOLID: SRP - focused on admin notification state management
 */
export function useAdminNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<FormattedAdminNotification[]>([]);
  const [counts, setCounts] = useState<AdminNotificationCounts>({
    total: 0,
    vehicles: 0,
    documents: 0,
    cancellations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [activeCategory, setActiveCategory] = useState<AdminNotificationCategory>("all");
  const channelRef = useRef<RealtimeChannel | null>(null);

  /**
   * Fetch notifications from the service
   */
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const [notifs, unreadCounts] = await Promise.all([
        adminNotificationService.getAdminNotifications({
          category: activeCategory,
          limit: 50,
        }),
        adminNotificationService.getUnreadCounts(),
      ]);

      setNotifications(notifs);
      setCounts(unreadCounts);
    } catch (err) {
      console.error("[useAdminNotifications] Error fetching:", err);
      setError(err instanceof Error ? err : new Error("Erreur inconnue"));
    } finally {
      setLoading(false);
    }
  }, [user, activeCategory]);

  /**
   * Refresh counts only (lightweight)
   */
  const refreshCounts = useCallback(async () => {
    if (!user) return;

    try {
      const unreadCounts = await adminNotificationService.getUnreadCounts();
      setCounts(unreadCounts);
    } catch (err) {
      console.error("[useAdminNotifications] Error refreshing counts:", err);
    }
  }, [user]);

  /**
   * Handle new notification from realtime
   */
  const handleNewNotification = useCallback(
    (payload: { eventType: string; new: Record<string, unknown> | null }) => {
      if (
        (payload.eventType === "INSERT" && payload.new) ||
        payload.eventType === "UPDATE" ||
        payload.eventType === "DELETE"
      ) {
        // Just refresh the list, notifications are handled globally by AdminNotificationBadgeContext
        fetchNotifications();
      }
    },
    [fetchNotifications]
  );

  /**
   * Set up realtime subscription
   */
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchNotifications();

    // Set up realtime subscription
    const channel = supabase
      .channel(`admin-notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Only handle admin notification types
          const newRecord = payload.new as Record<string, unknown> | null;
          const oldRecord = payload.old as Record<string, unknown> | null;
          const type = (newRecord?.type || oldRecord?.type) as AdminNotificationType | undefined;
          if (type && ADMIN_NOTIFICATION_TYPES.includes(type)) {
            handleNewNotification(payload);
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          console.log("[useAdminNotifications] Realtime connected");
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("[useAdminNotifications] Realtime error:", status);
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, fetchNotifications, handleNewNotification]);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await adminNotificationService.markAsRead(notificationId);

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
        );

        // Decrement counts
        const notification = notifications.find((n) => n.id === notificationId);
        if (notification && !notification.isRead) {
          setCounts((prev) => {
            const newCounts = { ...prev, total: Math.max(0, prev.total - 1) };
            switch (notification.category) {
              case "vehicles":
                newCounts.vehicles = Math.max(0, prev.vehicles - 1);
                break;
              case "documents":
                newCounts.documents = Math.max(0, prev.documents - 1);
                break;
              case "cancellations":
                newCounts.cancellations = Math.max(0, prev.cancellations - 1);
                break;
            }
            return newCounts;
          });
        }
      } catch (err) {
        console.error("[useAdminNotifications] Error marking as read:", err);
        toast.error("Impossible de marquer comme lu");
      }
    },
    [notifications]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await adminNotificationService.markAllAsRead();

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });

      toast.success("Toutes les notifications ont été marquées comme lues");
    } catch (err) {
      console.error("[useAdminNotifications] Error marking all as read:", err);
      toast.error("Impossible de marquer toutes les notifications comme lues");
    }
  }, []);

  /**
   * Mark all notifications in a category as read
   */
  const markCategoryAsRead = useCallback(
    async (category: AdminNotificationCategory) => {
      try {
        await adminNotificationService.markCategoryAsRead(category);

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.category === category || category === "all" ? { ...n, isRead: true } : n))
        );

        // Update counts
        if (category === "all") {
          setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });
        } else {
          setCounts((prev) => ({
            ...prev,
            total: Math.max(0, prev.total - prev[category]),
            [category]: 0,
          }));
        }

        toast.success("Notifications marquées comme lues");
      } catch (err) {
        console.error("[useAdminNotifications] Error marking category as read:", err);
        toast.error("Erreur lors du marquage");
      }
    },
    []
  );

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        const notification = notifications.find((n) => n.id === notificationId);

        await adminNotificationService.deleteNotification(notificationId);

        // Update local state
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

        // Update counts if was unread
        if (notification && !notification.isRead) {
          setCounts((prev) => {
            const newCounts = { ...prev, total: Math.max(0, prev.total - 1) };
            switch (notification.category) {
              case "vehicles":
                newCounts.vehicles = Math.max(0, prev.vehicles - 1);
                break;
              case "documents":
                newCounts.documents = Math.max(0, prev.documents - 1);
                break;
              case "cancellations":
                newCounts.cancellations = Math.max(0, prev.cancellations - 1);
                break;
            }
            return newCounts;
          });
        }
      } catch (err) {
        console.error("[useAdminNotifications] Error deleting:", err);
        toast.error("Impossible de supprimer la notification");
      }
    },
    [notifications]
  );

  /**
   * Change active category filter
   */
  const changeCategory = useCallback((category: AdminNotificationCategory) => {
    setActiveCategory(category);
  }, []);

  /**
   * Get notifications filtered by current category
   */
  const filteredNotifications =
    activeCategory === "all"
      ? notifications
      : notifications.filter((n) => n.category === activeCategory);

  return {
    notifications: filteredNotifications,
    allNotifications: notifications,
    counts,
    loading,
    error,
    activeCategory,
    changeCategory,
    markAsRead,
    markAllAsRead,
    markCategoryAsRead,
    deleteNotification,
    refresh: fetchNotifications,
    refreshCounts,
  };
}

